import PolicyLayout from "../components/PolicyLayout";

const sections = [
  {
    heading: "Preamble",
    paragraphs: [
      "The Board of Directors of Finncube Credit Services Pvt. Ltd. has adopted this Risk Management Policy covering practices relating to identification, assessment, monitoring, and mitigation of various risks to the business.",
      "The Risk Management Policy seeks to minimise unfavourable impact on business objectives, develop stakeholder value, and sustain and enhance the long-term competitive advantage of the Company.",
    ],
  },
  {
    heading: "Purpose",
    paragraphs: [
      "This policy has been framed in accordance with the risk management framework issued by the Reserve Bank of India through Master Direction DNBR.PD.008/03.10.119/2016-17 dated September 1, 2016 and related amendments.",
      "The purpose of this policy is to address unanticipated and unintended losses to the human resources and financial assets of the Company without unnecessarily limiting activities that advance its mission and goals.",
      "Finncube Credit Services Pvt. Ltd. has introduced effective risk management systems that address issues relating to various risks. Effective management of risk is vital to the continued growth of the Company.",
    ],
  },
  {
    heading: "Principles",
    items: [
      "Risk management must create and protect value.",
      "Risk management is integrated into organisational processes.",
      "Explicit risk management helps decision-makers make informed choices.",
      "Risk management is focused on sources of uncertainty around achievement of objectives.",
      "Risk management must be tailored to the context and fit for purpose.",
      "Risk management is dynamic, iterative, and responsive to change.",
    ],
  },
  {
    heading: "Definitions",
    items: [
      "Board means the Board of Directors of the Company.",
      "Company means Finncube Credit Services Pvt. Ltd.",
      "Directors mean individual Director or Directors on the Board of the Company.",
      "Policy means this Risk Management Policy.",
      "RBI means Reserve Bank of India.",
      "RCM means Risk Control Matrix.",
    ],
  },
  {
    heading: "Policy Framework",
    paragraphs: [
      "Finncube Credit Services Pvt. Ltd. recognises risk management as one of the key drivers of growth and enhancement of corporate governance.",
    ],
    items: [
      "To continuously strive to identify available risks in the organisation that directly or indirectly affect its functioning.",
      "To ensure protection of the rights and values of shareholders by establishing a well-organised risk management framework.",
      "To select, maintain, and enhance the risk management tools used by the programme to provide analyses that inform and support organisational investment actions.",
    ],
  },
  {
    heading: "Identification, Measurement and Assessment of Risk",
    items: [
      "Management, as delegated by the Board, is responsible for operationalising the Risk Management Programme and ensuring formal procedures are in place to identify and define risk with inputs across businesses.",
      "Measurement of risk is completed using both quantitative and qualitative methods based on likelihood and impact criteria developed by Management and reviewed by the Board.",
      "Management has identified inherent and residual risks and divided them according to likelihood and business impact.",
      "Identified risks include Strategic Risk, Operational Risk, Market Risk, Financial Risk, Reputational Risk, Credit and Concentration Risk, Regulatory and Compliance Risk, and Human Resource Risk.",
    ],
  },
  {
    heading: "Strategic Risk",
    paragraphs: [
      "Strategic risk relates to overall business strategies and the related economic and business environment. It includes risk to earnings and capital arising from lack of responsiveness to changes in the environment, adverse business decisions, wrong strategies, or unsuitable choices.",
      "Management remains proactive in responding to economic and business changes, regularly discusses business strategies with senior officials, and refers important strategic matters to the Board for deliberation and collective wisdom.",
      "The Company has also obtained Directors' and Officers' liability insurance to protect the Board and senior management in relation to decisions taken during the course of their duties.",
    ],
  },
  {
    heading: "Reputational Risk",
    paragraphs: [
      "Reputational risk relates to adverse perception of the Company by customers, counterparties, shareholders, investors, regulators, and other stakeholders.",
      "This risk may arise from unethical practices, regulatory actions, customer dissatisfaction, complaints, or negative publicity and may significantly impact the Company’s reputation and brand equity.",
    ],
    items: [
      "Potential triggers include non-compliance with regulations, customer dissatisfaction, and misrepresentation of facts and figures in public.",
      "Mitigation includes compliance with Fair Practices Code, a defined Grievance Redressal Mechanism, non-coercive delinquency management practices, stringent selection criteria for employees and vendors, market reference checks, and legal obligations such as non-disclosure clauses in contracts.",
    ],
  },
  {
    heading: "Market Risk",
    paragraphs: [
      "Market risk arises out of choices made on markets, resources, and delivery models that may affect long-term competitive advantage.",
      "It also includes risks inherent in the Company’s industry, including competitive structure, technological landscape, economic linkages, and regulatory structure.",
      "Management regularly reviews the business model and competitive positioning and makes changes where necessary to remain effective and competitive.",
    ],
  },
  {
    heading: "Operational Risk",
    paragraphs: [
      "Operational risk includes risks inherent to business operations such as client acquisition, service delivery, business support activities, information security, physical security, human resource issues, fraud, technology failure, outsourcing, and business disruptions.",
    ],
    items: [
      "The Company maintains original documents in a fire-proof vault and uses a specialised record management company for bar-coded physical storage.",
      "Scanned copies of loan documents are also maintained for easier retrieval, especially for audit purposes.",
      "The Company operates a Whistle Blower and Fraud Prevention framework with a no-retaliation approach and formal investigation, hearing, and closure procedures.",
      "Internal audits at branch and corporate levels are conducted by an independent audit firm appointed by the Board, with findings presented to the Audit Committee.",
      "An external advisory firm supports internal financial controls relating to governance, IT infrastructure, enterprise risk management, anti-fraud, financial reporting, process controls, and continuous monitoring.",
      "The Company uses cloud-based technology hosted in secure data centres with mirrored redundancies and backup connectivity arrangements.",
      "The Company has established an Outsourcing Policy in line with RBI guidelines and performs detailed checks before outsourcing work to any vendor.",
    ],
  },
  {
    heading: "Financial Risk",
    paragraphs: [
      "Financial risk includes movement in interest rates and liquidity risks inherent to the business.",
    ],
    items: [
      "Interest Rate Risk: The Company funds fixed-rate loans and manages Net Interest Income and Margin risk by pricing products to adequately cover interest rate exposure. A prudent and conservative risk mitigation strategy is followed while finalising lending rates.",
      "Liquidity Risk: Liquidity management is critical and is measured through maturity or cash flow mismatches, maturity ladders, and cumulative surplus or deficit analysis.",
      "Maturity mismatch is a key source of liquidity risk, arising when assets and liabilities are not aligned in tenor.",
      "Reliance on external sources of funds creates funding concentration risk, asset-liability mismatch risk, market perception risk, and leverage risk.",
      "Mitigation measures include regular ALM meetings, regular lender exposure updates, floating-rate borrowing against fixed-rate lending, defined leverage targets, and maintaining healthy capital adequacy levels significantly above regulatory thresholds.",
    ],
  },
  {
    heading: "Credit and Concentration Risk",
    paragraphs: [
      "Credit risk arises from repayment default by borrowers and counterparties and from inability to fully realise collateral value due to legal proceedings, delays, or changes in collateral value.",
      "Portfolio concentration risk arises where exposure becomes too high to a particular business segment, industry, geography, or other concentration point.",
    ],
    items: [
      "Mitigation includes a structured and standardised credit approval process, strong systems, effective training, legal and technical due diligence, active monitoring, and senior-level credit risk management.",
      "The Company intends to maintain diversified advances across sectors and geographies and has expanded into areas such as consumer durables, personal loans, and consumption loans.",
      "Credit appraisal also considers credit history, project size, existing borrowings, third-party verifications, and protective loan covenants.",
    ],
  },
  {
    heading: "Regulatory and Compliance Risk",
    paragraphs: [
      "The Company is exposed to risk arising from various statutes, regulations, lender covenants, and supervisory requirements.",
      "Non-compliance may result in penalties, regulatory action, and reputational damage.",
    ],
    items: [
      "Key risks include non-compliance with RBI regulations, statutory regulations, and lender covenants.",
      "Mitigation measures include use of AVANTIS compliance management system with in-built workflows to track, update, and monitor compliance.",
      "Internal audit reviews the compliance function quarterly and regulatory compliances are examined in detail.",
      "A quarterly compliance certificate certified by the CFO is submitted to the Board.",
    ],
  },
  {
    heading: "Human Resource Risk",
    paragraphs: [
      "Human resource risk arises where employee-related factors such as safety, security, compensation, retention, conduct, and engagement are not handled appropriately.",
      "The Company seeks to minimise these risks by providing equal opportunity, building belonging and commitment, and offering training beyond core specialisations.",
      "Employees are encouraged to contribute suggestions on innovation, cost saving, and other positive ideas, and compensation is linked to fair appraisal, peer comparison, and individual performance.",
      "Mitigation includes ongoing HR policies and initiatives aimed at retaining and motivating talent.",
    ],
  },
  {
    heading: "Responsibility",
    paragraphs: [
      "Responsibility for risk management is shared across the organisation, and controlling risk through a formal programme is necessary for organisational well-being, safety, operational continuity, and stakeholder value.",
    ],
    items: [
      "The Board is responsible for satisfying itself annually, or more frequently as required, that management has developed and implemented an effective risk management framework.",
      "Detailed work on this task is delegated to the Audit Committee and reviewed by the full Board.",
      "The Audit Committee assists the Board in overseeing the risk profile of the Company and management’s actions in identification, management, and reporting of material business risks.",
    ],
  },
  {
    heading: "Reporting Requirements",
    paragraphs: [
      "The enterprise reporting process will evolve as requirements and risk management practices evolve.",
      "Annual reporting shall include a risk profile setting out the most significant risks faced by the enterprise.",
    ],
    items: [
      "For each key risk, reporting should describe the risk, document key mitigation activities and controls, identify residual risk, refer to action plans addressing weaknesses, and draft a risk appetite statement for each key strategic risk.",
      "On a quarterly basis, updated information materially affecting the risk profile, such as market developments, shall be provided to the Board through the Audit Committee.",
      "Risk assessments shall consider all relevant factors before determining overall risk level and the appropriate mitigation to be applied.",
      "Such assessments shall be documented, updated regularly, and made available to competent authorities and self-regulating bodies whenever required.",
      "Where risk perception changes in the case of a Politically Exposed Person borrower, the risk assessment shall be revisited to determine whether continued exposure is prudent. If funds are not considered safe, they should be recalled without delay.",
    ],
  },
];

function RiskManagementPolicy() {
  return (
    <PolicyLayout
      title="Risk Management Policy"
      intro="This policy sets out the framework followed by Finncube Credit Services Pvt. Ltd. for identifying, assessing, monitoring, reporting, and mitigating strategic, financial, operational, regulatory, and other business risks."
      lastUpdated="April 10, 2026"
      sections={sections}
    />
  );
}

export default RiskManagementPolicy;
